/**
 * @license
 * Copyright 厦门乾元盛世科技有限公司 All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file.
 */


import { Component, Input, Output, EventEmitter, ElementRef, Renderer, HostBinding, ViewEncapsulation } from '@angular/core';
import { AfterViewInit, ContentChild, ViewChild, Inject, Optional, forwardRef } from '@angular/core';
import { NgControl, NG_VALUE_ACCESSOR, COMPOSITION_BUFFER_MODE } from '@angular/forms';
import KeyCode from '../util/key.code';
import calculateNodeHeight from './calculateNodeHeight';
import classNames from 'classnames';

import { FormControl } from './form.control';
import { InputPrefix } from './input.prefix';
import { InputSuffix } from './input.suffix';

import { Subject } from 'rxjs/Subject';
import'rxjs/add/operator/debounceTime';

const /** @type {?} */ INPUT_CONTROL_VALUE_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => InputBox),
    multi: true
};


export interface AutoSizeType {
    minRows?: number;
    maxRows?: number;
};
export class InputBox extends FormControl implements AfterViewInit {
/**
 * 样式前缀
 */
prefixCls = 'ant-input';
/**
 * 用户CSS样式
 */
class: string;
/**
 * 声明 input 类型，同原生 input 标签的 type 属性。另外提供 `type="textarea"`
 */
type = 'text';
/**
 * 控件大小。注：标准表单内的输入框大小限制为 `large`。可选 `large` `default` `small`。
 */
size: 'large' | 'default' | 'small';
/**
 * 输入框的placeholder
 */
placeholder: string;
/**
 * 是否只读状态，默认为 false
 */
readOnly = false;
/**
 * 带有前缀图标的 input，也可以通过使用属性为“input-prefix”的元素来达到同样效果
 */
prefix: string;
/**
 * 带有后缀图标的 input，也可以通过使用属性为“input-suffix”的元素来达到同样效果
 */
suffix: string;
/**
 * 自适应内容高度，只对 `type="textarea"` 有效，可设置为 `true|false` 或对象：`{ minRows: 2, maxRows: 6 }`
 */
autosize: boolean | AutoSizeType = false;

     autoComplete: 'on' | 'off';

     spellCheck: boolean;
/**
 * maxlength 属性规定输入字段的最大长度，以字符个数计。<br>
 * 注释：maxlength 属性与 <input type="text"> 或 <input type="password"> 配合使用。
 */
maxLength: number;
/**
 * minlength 属性规定输入字段的最小长度，以字符个数计。<br>
 * 注释：minlength 属性与 <input type="text"> 或 <input type="password"> 配合使用。
 */
minLength: number;
/**
 * max 属性规定输入字段所允许的最大值。<br>
 * 注释：max 和 min 属性适用于以下 <input> 类型：number, range, date, datetime, datetime-local, month, time 以及 week。
 */
max: number | Date;
/**
 * min 属性规定输入字段所允许的最小值。<br>
 * 注释：max 和 min 属性适用于以下 <input> 类型：number, range, date, datetime, datetime-local, month, time 以及 week。
 */
min: number | Date;
/**
 * 是否必填
 */
required = false;
/**
 * pattern 属性规定用于验证输入字段的模式（正则表达式）。<br>
 * 注释：pattern 属性适用于以下 <input> 类型：text, search, url, telephone, email 以及 password 。
 */
pattern: string;
/**
 * 行数，当类型是textarea时
 */
rows = 3;
/**
 * 默认情况下，输入框的focus、blur事件是不传播的，因此当我们控制内部的输入框后，需要发射自己的focus、blur事件
 * 
 * \@output {event} Expression to call when the input no longer has focus
 */
blur: EventEmitter<Event> = new EventEmitter<Event>();
/**
 * 默认情况下，输入框的focus、blur事件是不传播的，因此当我们控制内部的输入框后，需要发射自己的focus、blur事件
 * 
 * \@output {event} Expression to call when the input has focus
 */
focus: EventEmitter<Event> = new EventEmitter<Event>();
/**
 * 内部输入框的内容，用于绑定内部的 ngModel 值
 * @param {?} value
 * @return {?}
 */
set innerValue(value: any) {
        if (this._value !== value) {
            // processValue()用于转换接收内部view控件产生的值和外部通过@Input传入的值
            this._value = this.processValue(value);
            // view1 -> model -> onChange -> view2 (outside world) (ie. NgModel on this control)
            // view1 is 我们在template中定义的组件生成的view
            // view2 is 别人使用当前组件在应用中生成的view
            this.onChange(value);
        }
    }
/**
 * The value of the input ngModel
 * 
 * \@internal (model -> view2)
 * @return {?}
 */
get innerValue() {
        return this._value;
    }
/**
 * The internal data model
 * 
 * \@internal
 */
private _value: any = '';
private valueChangeEmiter = new Subject<any>();
/**
 * 长文本框的样式：用于当输入变化时重新计算文本框的高度 (如在 `autosize` 为 `true` 的情况下)
 */
public textareaStyles: any;


    // 前后缀
     inputPrefix: InputPrefix;
     inputSuffix: InputSuffix;

    // 实际输入控件(<input>)
     state: NgControl;
     input: ElementRef;
/**
 * @param {?} renderer
 * @param {?} elementRef
 * @param {?} compositionMode
 */
constructor(
private renderer: Renderer,
private elementRef: ElementRef,
private compositionMode: boolean) {
        super(renderer, elementRef, compositionMode);

        // wait for 300ms pause in events
        this.valueChangeEmiter.debounceTime(300).subscribe(() => {
            this.resizeTextarea();
        });
    }
/**
 * 对用户输入的值进行预处理，如文本转数字等
 * @param {?} value
 * @return {?}
 */
processValue(value: any): any {
        return value;
    }
/**
 * Write a new value to the element.
 * 
 * \@Override (From ControlValueAccessor interface)
 * @param {?} value
 * @return {?}
 */
writeValue(value: any): void {
        if (value !== null) {
            // view2 (outside world) (ie. NgModel on this control) -> model -> view1
            // view1 is 我们在template中定义的组件生成的view
            // view2 is 别人使用当前组件在应用中生成的view
            this.innerValue = value;
        }
    }
/**
 * The underlying host native element
 * @return {?}
 */
getHostElement(): HTMLElement {
        return /** @type {?} */(( this.elementRef.nativeElement as HTMLElement));
    }
/**
 * invalid样式
 * @return {?}
 */
get warnCls(): boolean {
        return this.state && this.state.invalid;
    }
/**
 * @return {?}
 */
getInputClass(): string {
        return classNames(this.prefixCls, {
            [`${this.prefixCls}-sm`]: this.size === 'small',
            [`${this.prefixCls}-lg`]: this.size === 'large'
        }, this.class);
    }
/**
 * @return {?}
 */
get inputClass(): string {
        return this.getInputClass();
    }
/**
 * @return {?}
 */
get affixWrapperClass(): string {
        return `${this.prefixCls}-affix-wrapper`;
    }
/**
 * @return {?}
 */
get affixPrefixClass(): string {
        return `${this.prefixCls}-prefix`;
    }
/**
 * @return {?}
 */
get affixSuffixClass(): string {
        return `${this.prefixCls}-suffix`;
    }
/**
 * @return {?}
 */
ngAfterViewInit(): void {
        setTimeout(() => {
            this.resizeTextarea();
        }, 10);
    }
/**
 * 当前字符总长度
 * @return {?}
 */
get charLength(): number {
        return (this._value && this._value.length) || 0;
    }
/**
 * @param {?} event
 * @return {?}
 */
handleKeyDown(event: KeyboardEvent): void {
        if (this.maxLength && this.charLength >= this.maxLength) {
            const /** @type {?} */ keyCode = event.keyCode;
            if (keyCode !== KeyCode.DELETE && keyCode !== KeyCode.BACKSPACE) {
                event.preventDefault(); // 禁止输入
            }
        }
    }
/**
 * @param {?} ev
 * @return {?}
 */
handleKeyUp(ev: UIEvent): void {
        this.valueChangeEmiter.next(ev);
    }
/**
 * @param {?} event
 * @return {?}
 */
handleTextareaChange(event: Event): void {
        this.resizeTextarea();
    }
/**
 * @return {?}
 */
resizeTextarea(): void {
        if (this.type !== 'textarea' || !this.autosize) {
            return;
        }
        const /** @type {?} */ minRows = this.autosize ? ( /** @type {?} */((this.autosize as AutoSizeType))).minRows : null;
        const /** @type {?} */ maxRows = this.autosize ? ( /** @type {?} */((this.autosize as AutoSizeType))).maxRows : null;
        this.textareaStyles = calculateNodeHeight( /** @type {?} */((
            this.input.nativeElement as HTMLTextAreaElement)), false, minRows, maxRows);
    }
/**
 * @param {?} event
 * @return {?}
 */
onBlur(event: UIEvent) {
        this.onTouched(); // set your control to 'touched'
        this.blur.emit(event);
    }
/**
 * @param {?} event
 * @return {?}
 */
onFocus(event: UIEvent) {
        this.focus.emit(event);
    }
/**
 * @return {?}
 */
doFocus(): void {
        this.input.nativeElement.focus();
    }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
    selector: 'ant-input',
    templateUrl: './input.html',
    styleUrls: ['./style/index.scss'],
    encapsulation: ViewEncapsulation.None,
    providers: [INPUT_CONTROL_VALUE_ACCESSOR]
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: Renderer, decorators: [{ type: Inject, args: [Renderer, ] }, ]},
{type: ElementRef, decorators: [{ type: Inject, args: [ElementRef, ] }, ]},
{type: undefined, decorators: [{ type: Optional }, { type: Inject, args: [COMPOSITION_BUFFER_MODE, ] }, ]},
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'prefixCls': [{ type: Input },],
'class': [{ type: Input },],
'type': [{ type: Input },],
'size': [{ type: Input },],
'placeholder': [{ type: Input },],
'readOnly': [{ type: Input },],
'prefix': [{ type: Input },],
'suffix': [{ type: Input },],
'autosize': [{ type: Input },],
'autoComplete': [{ type: Input },],
'spellCheck': [{ type: Input },],
'maxLength': [{ type: Input },],
'minLength': [{ type: Input },],
'max': [{ type: Input },],
'min': [{ type: Input },],
'required': [{ type: Input },],
'pattern': [{ type: Input },],
'rows': [{ type: Input },],
'blur': [{ type: Output },],
'focus': [{ type: Output },],
'inputPrefix': [{ type: ContentChild, args: [InputPrefix, ] },],
'inputSuffix': [{ type: ContentChild, args: [InputSuffix, ] },],
'state': [{ type: ContentChild, args: [NgControl, ] },],
'input': [{ type: ViewChild, args: ['input', ] },],
'warnCls': [{ type: HostBinding, args: ['class.ant-input-invalid', ] },],
};
}

function InputBox_tsickle_Closure_declarations() {
/** @type {?} */
InputBox.decorators;
/**
 * @nocollapse
 * @type {?}
 */
InputBox.ctorParameters;
/** @type {?} */
InputBox.propDecorators;
/**
 * 样式前缀
 * @type {?}
 */
InputBox.prototype.prefixCls;
/**
 * 用户CSS样式
 * @type {?}
 */
InputBox.prototype.class;
/**
 * 声明 input 类型，同原生 input 标签的 type 属性。另外提供 `type="textarea"`
 * @type {?}
 */
InputBox.prototype.type;
/**
 * 控件大小。注：标准表单内的输入框大小限制为 `large`。可选 `large` `default` `small`。
 * @type {?}
 */
InputBox.prototype.size;
/**
 * 输入框的placeholder
 * @type {?}
 */
InputBox.prototype.placeholder;
/**
 * 是否只读状态，默认为 false
 * @type {?}
 */
InputBox.prototype.readOnly;
/**
 * 带有前缀图标的 input，也可以通过使用属性为“input-prefix”的元素来达到同样效果
 * @type {?}
 */
InputBox.prototype.prefix;
/**
 * 带有后缀图标的 input，也可以通过使用属性为“input-suffix”的元素来达到同样效果
 * @type {?}
 */
InputBox.prototype.suffix;
/**
 * 自适应内容高度，只对 `type="textarea"` 有效，可设置为 `true|false` 或对象：`{ minRows: 2, maxRows: 6 }`
 * @type {?}
 */
InputBox.prototype.autosize;
/** @type {?} */
InputBox.prototype.autoComplete;
/** @type {?} */
InputBox.prototype.spellCheck;
/**
 * maxlength 属性规定输入字段的最大长度，以字符个数计。<br>
 * 注释：maxlength 属性与 <input type="text"> 或 <input type="password"> 配合使用。
 * @type {?}
 */
InputBox.prototype.maxLength;
/**
 * minlength 属性规定输入字段的最小长度，以字符个数计。<br>
 * 注释：minlength 属性与 <input type="text"> 或 <input type="password"> 配合使用。
 * @type {?}
 */
InputBox.prototype.minLength;
/**
 * max 属性规定输入字段所允许的最大值。<br>
 * 注释：max 和 min 属性适用于以下 <input> 类型：number, range, date, datetime, datetime-local, month, time 以及 week。
 * @type {?}
 */
InputBox.prototype.max;
/**
 * min 属性规定输入字段所允许的最小值。<br>
 * 注释：max 和 min 属性适用于以下 <input> 类型：number, range, date, datetime, datetime-local, month, time 以及 week。
 * @type {?}
 */
InputBox.prototype.min;
/**
 * 是否必填
 * @type {?}
 */
InputBox.prototype.required;
/**
 * pattern 属性规定用于验证输入字段的模式（正则表达式）。<br>
 * 注释：pattern 属性适用于以下 <input> 类型：text, search, url, telephone, email 以及 password 。
 * @type {?}
 */
InputBox.prototype.pattern;
/**
 * 行数，当类型是textarea时
 * @type {?}
 */
InputBox.prototype.rows;
/**
 * 默认情况下，输入框的focus、blur事件是不传播的，因此当我们控制内部的输入框后，需要发射自己的focus、blur事件
 * 
 * \@output {event} Expression to call when the input no longer has focus
 * @type {?}
 */
InputBox.prototype.blur;
/**
 * 默认情况下，输入框的focus、blur事件是不传播的，因此当我们控制内部的输入框后，需要发射自己的focus、blur事件
 * 
 * \@output {event} Expression to call when the input has focus
 * @type {?}
 */
InputBox.prototype.focus;
/**
 * The internal data model
 * 
 * \@internal
 * @type {?}
 */
InputBox.prototype._value;
/** @type {?} */
InputBox.prototype.valueChangeEmiter;
/**
 * 长文本框的样式：用于当输入变化时重新计算文本框的高度 (如在 `autosize` 为 `true` 的情况下)
 * @type {?}
 */
InputBox.prototype.textareaStyles;
/** @type {?} */
InputBox.prototype.inputPrefix;
/** @type {?} */
InputBox.prototype.inputSuffix;
/** @type {?} */
InputBox.prototype.state;
/** @type {?} */
InputBox.prototype.input;
/** @type {?} */
InputBox.prototype.renderer;
/** @type {?} */
InputBox.prototype.elementRef;
/** @type {?} */
InputBox.prototype.compositionMode;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
